import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import twilioDevice from "@/lib/twilioDevice";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Phone, User, Buildings, Broadcast, Pause, CheckCircle, Clock } from "@phosphor-icons/react";

const OUTCOMES = [
    { v: "connected|interested", r: "connected", d: "interested", label: "Interested" },
    { v: "connected|converted", r: "connected", d: "converted", label: "Converted" },
    { v: "connected|callback", r: "connected", d: "callback", label: "Callback" },
    { v: "connected|not_interested", r: "connected", d: "not_interested", label: "Not Interested" },
];

export default function PredictiveRun({ campaignId, campaign }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [state, setState] = useState({ ready: false, on_call: false, active_record: null, agents_ready: 0, agents_available: 0, in_flight: 0, pending: 0, pending_due: 0, next_scheduled_at: null, abandon_rate: 0, abandon_threshold: 0.03, throttled: false });
    const [ready, setReady] = useState(false);
    const [outcome, setOutcome] = useState("");
    const [callbackAt, setCallbackAt] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const readyRef = useRef(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`/campaigns/${campaignId}/predictive/state`);
            setState(data);
        } catch { /* ignore */ }
    }, [campaignId]);

    const tick = useCallback(async () => {
        if (!readyRef.current) return;
        try { await api.post(`/campaigns/${campaignId}/predictive/tick`); } catch { /* ignore */ }
    }, [campaignId]);

    useEffect(() => {
        const s = setInterval(refresh, 4000);
        const t = setInterval(tick, 4000);
        refresh();
        return () => { clearInterval(s); clearInterval(t); };
    }, [refresh, tick]);

    const goReady = async () => {
        // Predictive agents only RECEIVE calls, so the mic-permission prompt that
        // placeCall() normally triggers never fires. Request it here (on the user's
        // click) so auto-accepted incoming calls can establish two-way audio.
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
        } catch (e) {
            toast.error("Microphone access is required to take calls. Please allow it and try again.");
            return;
        }
        // The Twilio Device was created before mic permission (agent never dialed
        // out), so its AudioHelper found no devices ("Devices not found: default")
        // and audio was dead. Now that permission is granted, force-enumerate and
        // rebuild the Device so it binds the real mic + speaker.
        try {
            await navigator.mediaDevices.enumerateDevices();
            if (user?.id) await twilioDevice.reinit(`agent_${user.id}`);
            await twilioDevice.primeAudio();
        } catch (e) { console.warn("[predictive] audio prime failed", e); }
        try {
            await api.post(`/campaigns/${campaignId}/predictive/ready`, { ready: true });
            readyRef.current = true; setReady(true);
            twilioDevice.setAutoAccept(true);
            toast.success("You're ready — calls will connect to you automatically");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    };

    const goPause = async () => {
        try {
            await api.post(`/campaigns/${campaignId}/predictive/ready`, { ready: false });
            readyRef.current = false; setReady(false);
            twilioDevice.setAutoAccept(false);
            toast("Paused — you won't receive new calls");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    };

    useEffect(() => () => { twilioDevice.setAutoAccept(false); if (readyRef.current) api.post(`/campaigns/${campaignId}/predictive/ready`, { ready: false }).catch(() => {}); }, [campaignId]);

    // Auto-pause once the campaign is fully exhausted (nothing left to dial and
    // nothing in flight) so the workspace stops "waiting"/dialing pointlessly.
    useEffect(() => {
        if (ready && !state.active_record && !state.on_call
            && (state.pending || 0) === 0 && (state.in_flight || 0) === 0) {
            goPause();
        }
    }, [ready, state.pending, state.in_flight, state.active_record, state.on_call]);

    const active = state.active_record;
    const exhausted = !active && !state.on_call && (state.pending || 0) === 0 && (state.in_flight || 0) === 0;
    const noneDueNow = !active && (state.pending_due || 0) === 0 && (state.in_flight || 0) === 0 && (state.pending || 0) > 0;
    const saveDisposition = async () => {
        if (!active) return;
        if (!outcome) { toast.error("Select an outcome"); return; }
        const o = OUTCOMES.find((x) => x.v === outcome);
        if (o.d === "callback" && !callbackAt) { toast.error("Pick a callback date & time"); return; }
        setSaving(true);
        try {
            const payload = { result: o.r, disposition: o.d, notes };
            if (o.d === "callback" && callbackAt) payload.callback_at = new Date(callbackAt).toISOString();
            const res = await api.post(`/campaigns/${campaignId}/records/${active.id}/disposition`, payload);
            toast.success(res.data?.outcome || "Saved — ready for next call");
            setOutcome(""); setCallbackAt(""); setNotes("");
            await api.post(`/campaigns/${campaignId}/predictive/ready`, { ready: true }).catch(() => {});
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setSaving(false); }
    };

    const Stat = ({ label, value, testid }) => (
        <div className="rounded-xl border border-stone-200 bg-white px-4 py-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone2">{label}</div>
            <div data-testid={testid} className="mt-0.5 text-2xl font-bold text-ink">{value}</div>
        </div>
    );

    return (
        <AppShell>
            <div className="mx-auto max-w-4xl px-6 py-8" data-testid="predictive-run">
                <button onClick={() => navigate(`/campaigns/${campaignId}`)} className="mb-4 inline-flex items-center gap-1 text-sm text-stone2 hover:text-ink"><ArrowLeft size={15} /> Back to campaign</button>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Broadcast size={26} weight="fill" className="text-brand" />
                        <div>
                            <h1 className="font-heading text-2xl font-bold text-ink">{campaign?.name || "Predictive Dialer"}</h1>
                            <p className="text-sm text-stone2">Auto-dials multiple lines and connects answered calls to you.</p>
                        </div>
                    </div>
                    {exhausted
                        ? null
                        : ready
                            ? <button onClick={goPause} data-testid="predictive-pause" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-5 py-2.5 text-sm font-semibold hover:border-ink"><Pause size={16} weight="fill" /> Pause</button>
                            : <button onClick={goReady} data-testid="predictive-ready" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"><Phone size={16} weight="fill" /> Go Ready</button>}
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Stat label="Agents Ready" value={state.agents_ready} testid="stat-agents-ready" />
                    <Stat label="Available" value={state.agents_available} testid="stat-agents-available" />
                    <Stat label="Lines Dialing" value={state.in_flight} testid="stat-in-flight" />
                    <Stat label="Remaining" value={state.pending} testid="stat-pending" />
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3" data-testid="abandon-guardrail">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-stone2">Abandon rate</span>
                        <span data-testid="abandon-rate" className={`font-heading text-lg font-bold ${state.abandon_rate > (state.abandon_threshold || 0.03) ? "text-red-600" : "text-emerald-600"}`}>
                            {((state.abandon_rate || 0) * 100).toFixed(1)}%
                        </span>
                        <span className="text-xs text-stone2">/ cap {(((state.abandon_threshold || 0.03)) * 100).toFixed(0)}%</span>
                    </div>
                    {state.throttled && (
                        <span data-testid="throttle-badge" className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" /> Auto-throttled — pacing reduced to protect compliance
                        </span>
                    )}
                </div>

                <div className="mt-6">
                    {active ? (
                        <div className="rounded-2xl border-2 border-brand/40 bg-brand/5 p-6" data-testid="predictive-active-call">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-brand"><span className="h-2 w-2 animate-pulse rounded-full bg-brand" /> Connected</div>
                            <div className="mt-3 flex items-center gap-3">
                                <div className="grid h-12 w-12 place-items-center rounded-full bg-ink text-white"><User size={22} weight="fill" /></div>
                                <div>
                                    <div className="text-lg font-bold text-ink" data-testid="active-name">{active.name || active.phone}</div>
                                    <div className="text-sm text-stone2 flex items-center gap-3">
                                        <span className="inline-flex items-center gap-1"><Phone size={13} /> {active.phone}</span>
                                        {active.company && <span className="inline-flex items-center gap-1"><Buildings size={13} /> {active.company}</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5">
                                <div className="text-[11px] font-bold uppercase tracking-widest text-stone2">Outcome</div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {OUTCOMES.map((o) => (
                                        <button key={o.v} data-testid={`pred-outcome-${o.d}`} onClick={() => setOutcome(o.v)}
                                            className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-all ${outcome === o.v ? "border-ink bg-ink text-white" : "border-stone-300 text-ink hover:border-ink"}`}>{o.label}</button>
                                    ))}
                                </div>
                                {OUTCOMES.find((x) => x.v === outcome)?.d === "callback" && (
                                    <div className="mt-3">
                                        <div className="text-[11px] font-bold uppercase tracking-widest text-stone2">Callback date &amp; time</div>
                                        <input type="datetime-local" data-testid="pred-callback-at" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)}
                                            className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                        <p className="mt-1 text-xs text-stone2">The lead reappears at this time, held for you first.</p>
                                    </div>
                                )}
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pred-notes" rows={2} placeholder="Notes…" className="mt-3 w-full resize-none rounded-lg border border-stone-300 p-2.5 text-sm outline-none focus:border-ink" />
                                <button onClick={saveDisposition} disabled={saving} data-testid="pred-save" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><CheckCircle size={16} weight="fill" /> {saving ? "Saving…" : "Save & Ready for next"}</button>
                            </div>
                        </div>
                    ) : exhausted ? (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-12 text-center" data-testid="predictive-complete">
                            <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-500" />
                            <p className="mt-3 font-heading text-xl font-bold text-ink">Campaign complete</p>
                            <p className="mt-1 text-sm text-stone2">Every lead in this campaign has been dialed — there's nothing left to call.</p>
                            <button onClick={() => navigate(`/campaigns/${campaignId}`)} className="mt-5 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white">Back to campaign</button>
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-dashed border-stone-300 p-12 text-center" data-testid="predictive-idle">
                            {!ready
                                ? <><Phone size={28} className="mx-auto text-stone-300" /><p className="mt-3 font-semibold text-ink">You're paused</p><p className="mt-1 text-sm text-stone2">Click “Go Ready” to start receiving predictive calls.</p></>
                                : noneDueNow
                                    ? <div data-testid="predictive-scheduled-wait"><Clock size={30} className="mx-auto text-stone-400" /><p className="mt-3 font-semibold text-ink">No numbers due to dial right now</p><p className="mt-1 text-sm text-stone2">{state.pending} scheduled callback{state.pending > 1 ? "s" : ""}/retr{state.pending > 1 ? "ies" : "y"} will connect when {state.pending > 1 ? "they're" : "it's"} due{state.next_scheduled_at ? ` — next at ${new Date(state.next_scheduled_at).toLocaleString()}` : ""}.</p></div>
                                    : <><span className="mx-auto block h-3 w-3 animate-ping rounded-full bg-brand" /><p className="mt-4 font-semibold text-ink">Waiting for the next connected call…</p><p className="mt-1 text-sm text-stone2">The system is dialing. Answered calls connect to you automatically.</p></>}
                        </div>
                    )}
                </div>
            </div>
        </AppShell>
    );
}
